import axios from 'axios';

import { env } from 'config';
import { TraderWorkStatus, UserRole, UserStatus } from 'enums';
import { QueryParams, Trader, TraderActiveOrdersCount, User } from 'types';
import { apiUtils } from 'utils';

const url = `${env.apiUrl}/traders`;

const getAllAsRole =
  (role: UserRole) =>
  async (params?: Pick<QueryParams, 'filters'>): Promise<Trader[]> =>
    (await apiUtils.getWithQueryParams(`${url}/${role}`, params)).data;

const getAllPaginatedAsRole =
  (role: UserRole) =>
  async (params?: Pick<QueryParams, 'filters'>): Promise<Trader[]> =>
    (await apiUtils.getWithQueryParams(`${url}/${role}/paginated`, params))
      .data;

const findOneAsRole =
  (role: UserRole) =>
  async (id: string): Promise<Trader> =>
    (await axios.get(`${url}/${role}/${id}`)).data;

const getMyInfo = async (): Promise<Trader> =>
  (await axios.get(`${url}/me`)).data;

const update = async ({
  data,
}: {
  data: Partial<
    Pick<Trader, 'workStatus' | 'payinMinOrderAmount' | 'payoutMaxActiveOrders'>
  >;
}): Promise<Trader> => (await axios.patch(`${url}/me`, data)).data;

const updateByRole =
  (role: UserRole) =>
  async ({
    id,
    trader,
  }: {
    id: string;
    trader: Partial<Trader>;
  }): Promise<Trader> =>
    (await axios.patch(`${url}/${role}/${id}`, trader)).data;

const updateWorkStatusAsRole =
  (role: UserRole) =>
  async ({
    id,
    workStatus,
  }: {
    id: string;
    workStatus: TraderWorkStatus;
  }): Promise<Trader> =>
    (await axios.patch(`${url}/${role}/${id}/work-status`, { workStatus }))
      .data;

const updateUserStatusAsOperator = async ({
  id,
  status,
}: {
  id: string;
  status: UserStatus;
}): Promise<User> =>
  (await axios.patch(`${url}/operator/${id}/user-status`, { status })).data;

const updateTrafficAsOperator = async ({
  id,
  data,
}: {
  id: string;
  data: { unableTakeOrders: boolean; unableTakeOrdersDuration?: number };
}): Promise<Trader> =>
  (await axios.patch(`${url}/operator/${id}/traffic`, data)).data;

const getActiveOrdersCountAsOperator = async (
  id: string,
): Promise<TraderActiveOrdersCount> =>
  (await axios.get(`${url}/operator/${id}/active-orders-count`)).data;

// TODO: extend from crud api
export const tradersApi = {
  getAllPaginatedAsRole,
  getAllAsRole,
  getMyInfo,
  findOneAsRole,
  updateByRole,
  update,
  updateWorkStatusAsRole,
  updateUserStatusAsOperator,
  updateTrafficAsOperator,
  getActiveOrdersCountAsOperator,
};
